import { useRef } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router"
import { useUser } from "@/apis/bff"
import { PreviewDocumentDialogWrapper } from "@/components/preview-document-dialog"
import { PreviewDocumentDialogFullScreenWrapper } from "@/components/preview-document-full-screen-dialog"
import { Header } from "@/layouts/header"
import { cn } from "@/lib/utils"
import { NewsBannerNotify } from "@/modules/admin/news/components/news-banner-notify"
import { LeadsOnDutyBanner } from "@/modules/leads-on-duty/components/leads-on-duty-banner"
import { Notes } from "@/modules/temporary-notes/notes"
import { usePartnerInVideoCallChannel } from "@/modules/video-call/services/usePartnerInVideoCallChannel"
import { AppointmentDrawer } from "@/modules/waiting-room/components/appointment-drawer"
import { Appointments } from "@/modules/waiting-room/components/appointment-table/appointments"
import {
  WaitingRoomPreviewHeader,
  WaitingRoomPreviewWrapper,
} from "@/modules/waiting-room/components/waiting-room-preview"
import { AppointmentProvider, useAppointmentContext } from "@/modules/waiting-room/contexts/appointment-context"
import { usePatientFileLockTakenOrReleasedSubscription } from "@/modules/waiting-room/services/usePatientFileLockTakenOrReleasedSubscription"
import useCallContextStore from "@/stores/useCallContextStore"

const WaitingRoom = () => {
  const user = useUser()
  usePatientFileLockTakenOrReleasedSubscription()
  usePartnerInVideoCallChannel()

  return (
    <AppointmentProvider role={user.role}>
      <HeaderContent />
      <AppointmentContent />
    </AppointmentProvider>
  )
}

const HeaderContent = () => {
  const { t } = useTranslation()
  const isOpeningWaitingRoomPreviewDialog = useAppointmentContext((state) => state.waitingRoomPreviewDialog.isOpening)

  return isOpeningWaitingRoomPreviewDialog ? (
    <WaitingRoomPreviewHeader />
  ) : (
    <div>
      <Header title={t("scheduling.waitingRoom.pageTitle")} />
      <NewsBannerNotify />
      <LeadsOnDutyBanner />
    </div>
  )
}

const AppointmentContent = () => {
  const tableRef = useRef<HTMLDivElement>(null)
  const callId = useCallContextStore((state) => state.callID)
  const isOpeningWaitingRoomPreviewDialog = useAppointmentContext((state) => state.waitingRoomPreviewDialog.isOpening)
  const [searchParams] = useSearchParams()
  const previewDocumentAppointmentId = searchParams.get("previewDocumentAppointmentId")
  const mediaFileId = searchParams.get("mediaFileId")

  return (
    <>
      {isOpeningWaitingRoomPreviewDialog && <WaitingRoomPreviewWrapper />}
      <div
        className={cn("grid-cols-1fr-auto relative grid grid-rows-1", isOpeningWaitingRoomPreviewDialog && "sr-only")}>
        <Appointments ref={tableRef} />
        <AppointmentDrawer tableRef={tableRef} isPreview={isOpeningWaitingRoomPreviewDialog} />
        {previewDocumentAppointmentId && mediaFileId ? (
          <PreviewDocumentDialogFullScreenWrapper
            previewDocumentAppointmentId={previewDocumentAppointmentId}
            mediaFileId={mediaFileId}
          />
        ) : (
          <PreviewDocumentDialogWrapper />
        )}

        {callId && <Notes />}
      </div>
    </>
  )
}

export { WaitingRoom }
